import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';
import { linkTextClasses } from './link.js';

/**
 * O bloco compacto do cabeçalho da `DeskModal` — o que quem opera consulta a
 * cada campo, e por isso fica à vista enquanto o formulário é preenchido.
 *
 * Um rótulo pequeno ("Ocorrido · do aviso"), as `SummaryCell` lado a lado,
 * uma mídia opcional (as miniaturas, `ThumbStrip`) e uma ação que abre a
 * gaveta com o resto ("Ver ocorrido"). Na mesa do sinistro são três usos com
 * a mesma peça — o ocorrido na Análise, a oficina no Reparo, a conta na
 * Indenização —, e um componente por departamento seria três desenhos do
 * mesmo bloco.
 *
 * **Compacto quer dizer uma linha por célula**: o texto que não cabe corta com
 * reticências e o inteiro vai na dica (`SummaryCell.title`). É a exceção à
 * regra "o valor quebra, não corta", e ela vale porque o inteiro está a um
 * clique, na gaveta.
 */
export function SummaryStrip({
  label,
  children,
  preview,
  action,
}: {
  label: string;
  /** As `SummaryCell`. */
  children: ReactNode;
  /** As miniaturas (`ThumbStrip`), ou a frase de quando não há. */
  preview?: ReactNode;
  /**
   * Abrir (e fechar) a gaveta. `expanded` diz se ela está aberta — o rótulo
   * troca ("Ver ocorrido" / "Fechar ocorrido") e a borda acende.
   */
  action?: { label: string; onClick: () => void; expanded?: boolean };
}) {
  return (
    <section
      aria-label={label}
      className={cn(
        'flex max-w-full min-w-0 flex-wrap items-center gap-x-3.5 gap-y-2 rounded-grande border bg-superficie-suave py-2 pr-2 pl-3.5 sm:flex-nowrap',
        action?.expanded ? 'border-primaria' : 'border-borda',
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[0.6875rem] font-semibold tracking-wide text-texto-tenue uppercase">
          {label}
        </span>
        <div className="flex min-w-0 divide-x divide-borda [&>*]:px-3 [&>*:first-child]:pl-0">
          {children}
        </div>
      </div>
      {preview}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          aria-expanded={action.expanded ?? false}
          className={linkTextClasses(
            'min-h-11 shrink-0 gap-1 self-stretch border-l border-borda pr-1.5 pl-3 text-xs font-bold active:opacity-80 max-sm:ml-auto',
          )}
        >
          {action.label}
          <Icon nome={action.expanded ? 'fechar' : 'seta-direita'} size={16} />
        </button>
      )}
    </section>
  );
}

/** Uma célula do `SummaryStrip`: o valor em destaque e um detalhe embaixo. */
export function SummaryCell({
  value,
  detail,
  title,
  noShrink = false,
}: {
  value: ReactNode;
  detail?: ReactNode;
  /** O texto inteiro, para a dica — a célula corta o que não cabe. */
  title?: string;
  /**
   * Para valor curto que não pode cortar — a data e a hora. Sem isso, no
   * telefone a célula da data encolhia até "30/0…" para dar lugar ao endereço.
   */
  noShrink?: boolean;
}) {
  return (
    <div
      title={title}
      className={cn('flex max-w-[15rem] min-w-0 flex-col', noShrink && 'shrink-0')}
    >
      <span className="truncate text-sm font-bold">{value}</span>
      {detail && <span className="truncate text-xs text-texto-suave">{detail}</span>}
    </div>
  );
}

export interface Photo {
  src: string;
  alt: string;
  /** A legenda embaixo da foto grande, no `PhotoViewer`. */
  caption?: string;
}

/**
 * Até `max` miniaturas e um "+N" sobre a última, ou a frase de quando não há
 * foto. Cada miniatura abre a gaveta naquela foto (`onOpen(i)`). O "+N" cobre
 * a última miniatura e conta ela junto, porque ela também está escondida; o
 * clique abre nela.
 */
export function ThumbStrip({
  photos,
  max = 3,
  onOpen,
  empty,
}: {
  photos: readonly Photo[];
  max?: number;
  onOpen: (index: number) => void;
  /** "O aviso chegou sem foto." */
  empty: ReactNode;
}) {
  if (photos.length === 0) {
    return <p className="max-w-[8rem] text-xs leading-snug text-texto-tenue">{empty}</p>;
  }
  const shown = photos.slice(0, max);
  const rest = photos.length - shown.length;
  return (
    <div className="flex shrink-0 gap-1.5">
      {shown.map((photo, i) => {
        const more = rest > 0 && i === shown.length - 1;
        return (
          <button
            key={`${photo.src}-${i}`}
            type="button"
            onClick={() => onOpen(i)}
            aria-label={
              more ? `Ver as outras ${rest + 1} fotos` : `Ver a foto: ${photo.alt}`
            }
            className="relative size-14 shrink-0 overflow-hidden rounded-borda border border-borda bg-borda hover:border-primaria active:scale-95"
          >
            <img src={photo.src} alt="" className="size-full object-cover" />
            {more && (
              <span className="absolute inset-0 grid place-items-center bg-veu text-sm font-bold text-texto-inverso">
                +{rest + 1}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
